import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import {
    MarkButton,
    MarkInput,
    MarkTimeInput,
    MarkHeading,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import useScheduleBlast from '../../../api/useScheduleBlast';
import useUpdateBlast from '../../../api/useUpdateBlast';
import {
    extractBlastErrorMessage,
    getScheduleError,
    getScheduleMinimum,
    getLocalSendTimeError,
    toDatetimeLocal,
    toUtcIso
} from '../blastFormUtils';
import type { BlastDetail } from '../../../types';
type Props = {
    blast: BlastDetail;
    onClose: () => void;
    onSuccess: () => void;
};
const defaultScheduledAt = (): string => {
    const d = new Date(Date.now() + 3600000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
};
const initialScheduledAt = (savedAt: string | null): string =>
    savedAt && new Date(savedAt) > new Date() ? toDatetimeLocal(savedAt) : defaultScheduledAt();
const ScheduleModal: React.FC<Props> = ({ blast, onClose, onSuccess }) => {
    const schedule = useScheduleBlast();
    const update = useUpdateBlast();
    const isPending = schedule.isPending || update.isPending;
    const mutationError = update.error ?? schedule.error;
    const headingId = useId();
    const scheduleFieldId = useId();
    const localTimeFieldId = useId();
    const isReschedule = blast.status === 'SCHEDULED';
    const [scheduledAt, setScheduledAt] = useState(() => initialScheduledAt(blast.scheduled_at));
    const [localSendTime, setLocalSendTime] = useState(blast.local_send_time ?? '');
    const [minimum, setMinimum] = useState(getScheduleMinimum);
    const [scheduleTouched, setScheduleTouched] = useState(false);
    const [localTimeTouched, setLocalTimeTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const scheduleError = getScheduleError(scheduledAt);
    const localTimeError = getLocalSendTimeError(localSendTime);
    const visibleScheduleError = scheduleTouched || submitAttempted ? scheduleError : null;
    const visibleLocalTimeError = localTimeTouched || submitAttempted ? localTimeError : null;
    const handleSubmit = () => {
        if (isPending) return;
        setSubmitAttempted(true);
        setMinimum(getScheduleMinimum());
        if (getScheduleError(scheduledAt) || getLocalSendTimeError(localSendTime)) return;
        const utc = toUtcIso(scheduledAt);
        update.reset();
        schedule.reset();
        const runSchedule = () => {
            setMinimum(getScheduleMinimum());
            if (getScheduleError(scheduledAt)) return;
            schedule.mutate(
                {
                    blast_id: blast.blast_id,
                    scheduled_at: utc,
                    ...(localSendTime && { local_send_time: localSendTime })
                },
                { onSuccess: () => onSuccess() }
            );
        };
        // schedule_blast only writes local_send_time when provided, so clearing needs update first.
        if (!isReschedule && blast.local_send_time && !localSendTime) {
            update.mutate(
                { blast_id: blast.blast_id, local_send_time: null },
                { onSuccess: runSchedule }
            );
        } else {
            runSchedule();
        }
    };
    return (
        <MarkModal
            onClose={onClose}
            isPending={isPending}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    {isReschedule ? 'Reschedule blast' : 'Schedule blast'}
                </MarkHeading>
                {blast.message_class === 'TRANSACTIONAL' && (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-warning)',
                            backgroundColor: 'var(--mark-warning-surface)',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '0.4rem',
                            marginBottom: '0.8rem'
                        }}
                    >
                        You will be recorded as transactional approver.
                    </P>
                )}
                <div className={layout.field}>
                    <MarkLabel
                        htmlFor={scheduleFieldId}
                        style={{
                            display: 'block',
                            marginBottom: '0.4rem'
                        }}
                    >
                        Send at (UTC)
                    </MarkLabel>
                    <MarkInput
                        id={scheduleFieldId}
                        type="datetime-local"
                        value={scheduledAt}
                        min={minimum}
                        onFocus={() => setMinimum(getScheduleMinimum())}
                        onBlur={() => setScheduleTouched(true)}
                        aria-invalid={!!visibleScheduleError}
                        aria-describedby={
                            visibleScheduleError ? `${scheduleFieldId}-error` : undefined
                        }
                        disabled={isPending}
                        onChange={(e) => {
                            setScheduledAt(e.currentTarget.value);
                        }}
                    />
                    {visibleScheduleError && (
                        <p
                            id={`${scheduleFieldId}-error`}
                            role="alert"
                            className={layout.fieldError}
                        >
                            {visibleScheduleError}
                        </p>
                    )}
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-muted)',
                            marginTop: '0.2rem'
                        }}
                    >
                        All times are UTC. Recipients whose profile has no timezone default to
                        America/Los_Angeles.
                    </P>
                </div>
                <div className={layout.field}>
                    <MarkLabel
                        htmlFor={localTimeFieldId}
                        style={{
                            display: 'block',
                            marginBottom: '0.4rem'
                        }}
                    >
                        Local send time (optional, HH:MM)
                    </MarkLabel>
                    <MarkTimeInput
                        id={localTimeFieldId}
                        onBlur={() => setLocalTimeTouched(true)}
                        aria-invalid={!!visibleLocalTimeError}
                        aria-describedby={
                            visibleLocalTimeError ? `${localTimeFieldId}-error` : undefined
                        }
                        value={localSendTime}
                        disabled={isPending}
                        readOnly={isReschedule && !!blast.local_send_time}
                        onChange={(e) => setLocalSendTime(e.currentTarget.value)}
                    />
                    {visibleLocalTimeError && (
                        <p
                            id={`${localTimeFieldId}-error`}
                            role="alert"
                            className={layout.fieldError}
                        >
                            {visibleLocalTimeError}
                        </p>
                    )}
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-muted)',
                            marginTop: '0.2rem',
                            maxWidth: '30rem'
                        }}
                    >
                        Recipient receives at this wall-clock time in their timezone. Never before
                        the UTC time above. Missed same-day sends go out the next day.
                    </P>
                </div>
                {mutationError && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {extractBlastErrorMessage(mutationError)}
                        </P>
                    </div>
                )}
                <div
                    style={{
                        marginTop: '1.6rem'
                    }}
                    className={layout.actions}
                >
                    <MarkButton type="button" onClick={onClose} disabled={isPending}>
                        Cancel
                    </MarkButton>
                    <MarkButton
                        type="button"
                        onClick={handleSubmit}
                        disabled={isPending}
                        variant="primary"
                    >
                        {isPending ? 'Scheduling…' : isReschedule ? 'Reschedule' : 'Schedule'}
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default ScheduleModal;
